'use client';

import { SyntheticEvent, useEffect, useRef, useState } from 'react';

import Icon2 from '@/components/elements/icon/icon2';
import { useToggle } from '@/hooks/use-toggle';
import { CodeTitle } from '@/types/general';
import { getScrollRem } from '@/utils/window-utils';

interface SelectProps<T extends CodeTitle> {
    options: T[];
    defaultValue: T;
    optionRenderer: (option: T) => JSX.Element;
    onClick: (option: T) => void;
    className?: { [key: string]: string };
    textClassName?: string;
    testId?: string;
}

export default function SelectHeader<T extends CodeTitle = CodeTitle>({
    textClassName = 'text-sm',
    ...props
}: SelectProps<T>) {
    const selectRef = useRef<HTMLDivElement>(null);
    const { toggleValue, handleToggle, setToggleValue } = useToggle(!(props.options && props.options.length > 0));
    const [topPos, setTopPos] = useState<string>(getScrollRem() >= 2 ? 'top-10' : 'top-14');

    const handleSelect = (e: SyntheticEvent, option: T) => {
        e.stopPropagation();
        props.onClick(option);
        setToggleValue(false);
    };

    // closes the dropdown when an outside click occurs
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setToggleValue(false);
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [setToggleValue]);

    // shifts the dropdown to align with the header
    useEffect(() => {
        const handlePageScroll = () => {
            setTopPos(getScrollRem() >= 2 ? 'top-10' : 'top-14');
        };
        window.addEventListener('scroll', handlePageScroll);
        return () => window.removeEventListener('scroll', handlePageScroll);
    }, []);

    return (
        <div ref={selectRef} className={`relative z-[1] flex text-sm ${props.className} ${textClassName}`}>
            <div
                data-testid={props.testId}
                onClick={handleToggle}
                className={`mx-4 flex w-full cursor-pointer justify-between gap-1.5 ${props.className && props.className.select}`}
            >
                <button type="button" className={`${props.className} ${textClassName}`}>
                    {props.defaultValue.title}
                </button>
                <Icon2
                    name="phi-caret-down-16"
                    iconClass={`h-5 w-5 transition-transform ${toggleValue ? '-scale-y-100' : ''}`}
                />
            </div>
            <ul
                className={`no-scrollbar absolute right-1/2 z-[1] flex max-h-52 w-max translate-x-1/2 flex-col items-start overflow-y-auto rounded-b-md border border-solid border-gray-500 bg-white py-2 ${topPos} ${toggleValue ? '' : 'hidden'}`}
            >
                {props.options.map((option: T) => (
                    <li
                        data-testid={`${props.testId}-${option.code}`}
                        key={option.code}
                        className="h-9 w-full cursor-pointer px-5 py-2 transition-colors duration-100 hover:bg-accent hover:bg-opacity-20"
                        onClick={(e) => handleSelect(e, option)}
                    >
                        {props.optionRenderer(option)}
                    </li>
                ))}
            </ul>
        </div>
    );
}
